import React, { useState } from 'react';

import type { SaveListItem, SaveProps } from '../../../types/save-types.js';

import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import Image from '../teasers/teaserComponents/Image.js';
import { useListsContext } from './ListsProvider.js';
import { itemIsAlreadyInList, sortSavedItems } from './utils.js';

type Action = {
  type: 'save' | 'remove';
  listName: string;
};

export const SaveInList: React.FC<SaveProps> = ({ item, handleOverlay }) => {
  const { savedItems, saveItem, deleteItems, hasError, statusMessage } =
    useListsContext();
  const { trackClickUIElement } = usePulseContext();
  const [previousAction, setPreviousAction] = useState<Action | null>(null);
  const [sortedSavedItems] = useState(() => sortSavedItems(savedItems));
  const [isOperationInProgress, setIsOperationInProgress] =
    useState<boolean>(false);

  const handleSaveItem = (listName: string, listContent: SaveListItem[]) => {
    if (item) {
      saveItem(
        {
          name: listName,
          content: listContent.concat(item),
          date: new Date().getTime(),
        },
        handleOverlay,
      );
    }
  };

  const handleRemoveItem = (listName: string, listContent: SaveListItem[]) => {
    if (item) {
      deleteItems(
        {
          name: listName,
          content: listContent,
          date: new Date().getTime(),
        },
        [item.id],
      );
    }
    setTimeout(function () {
      handleOverlay();
    }, 1000);
  };

  const handleSaveInList = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.target as HTMLButtonElement;

    const listName =
      button.value || (button.parentNode as HTMLButtonElement).value;
    const list = savedItems.find((l) => l.name === listName);
    const listContent = list ? list.content : [];

    const isInList = itemIsAlreadyInList(item, savedItems, listName);
    const actionType = isInList ? 'remove' : 'save';

    trackClickUIElement({
      object: {
        id: `${actionType}:saved-list`,
        name: 'List group in save overlay',
        elementType: 'Button',
      },
    }).finally(() => {
      if (actionType === 'save') {
        handleSaveItem(listName, listContent);
      } else {
        handleRemoveItem(listName, listContent);
      }
      setIsOperationInProgress(true);
      setPreviousAction({ type: actionType, listName });
    });
  };

  return (
    <>
      {sortedSavedItems.map((l, i) => {
        const thumbnail = l.content.at(-1);
        const savingAction =
          previousAction?.type === 'save' &&
          l.name === previousAction?.listName;
        const itemSaved =
          l.content.some((c) => c.id === item?.id) || savingAction;
        const saveSuccess = statusMessage && !hasError && savingAction;
        const removeActionFlag =
          previousAction?.type === 'remove' &&
          l.name === previousAction?.listName;

        if (hasError) {
          return null;
        }

        const classes = ['list-button'];
        if (saveSuccess) classes.push('save-success');
        if (removeActionFlag) classes.push('remove-action');

        return (
          <button
            key={`list-${i}`}
            onClick={handleSaveInList}
            value={l.name}
            className={classes.join(' ')}
            disabled={isOperationInProgress}
          >
            {thumbnail?.imageId ? (
              <Image
                imageAsset={{ id: thumbnail.imageId }}
                height={72}
                width={72}
                title={thumbnail.title}
              />
            ) : (
              <div className="no-image-placeholder" />
            )}
            <div className="saved-list-name">{l.name}</div>
            <div className="saved-items-count">{`${l.content.length} lagret`}</div>
            {itemSaved && !removeActionFlag && (
              <div className="already-saved" />
            )}
            {removeActionFlag && <div className="removing" />}
          </button>
        );
      })}
    </>
  );
};
